import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../App";
import { useTenant } from "../contexts/TenantContext";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Plus, Search, FileText, Calendar, User, Eye, Trash2, Send, ClipboardList, Edit, CheckCircle, XCircle, Bot, Loader2, Sparkles, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const findingTypes = [
  { value: "major", label: "Majör Uygunsuzluk" },
  { value: "minor", label: "Minör Uygunsuzluk" },
  { value: "observation", label: "Gözlem" },
  { value: "opportunity", label: "İyileştirme Fırsatı" }
];

const Reports = () => {
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [interactiveReports, setInteractiveReports] = useState([]);
  const [royalcertReports, setRoyalcertReports] = useState([]);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [auditorFilter, setAuditorFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportType, setReportType] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [auditors, setAuditors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [auditId, setAuditId] = useState(searchParams.get("audit") || "");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [findings, setFindings] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [newFindingType, setNewFindingType] = useState("");
  const [newFindingDesc, setNewFindingDesc] = useState("");
  const [newRecommendation, setNewRecommendation] = useState("");

  // AI Review state
  const [showAIReview, setShowAIReview] = useState(false);
  const [aiReviewLoading, setAiReviewLoading] = useState(false);
  const [aiReviewResult, setAiReviewResult] = useState(null);
  const [aiReviewReportId, setAiReviewReportId] = useState(null);

  // Approval state for Drive upload
  const [approvingReportId, setApprovingReportId] = useState(null);

  // Who can review reports
  const canReview = ["super_admin", "admin", "technical_manager"].includes(user?.role);

  useEffect(() => {
    fetchReports();
    fetchInteractiveReports();
    fetchRoyalcertReports();
    fetchAudits();
    fetchCustomers();
    fetchAuditors();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`, { withCredentials: true });
      setReports(response.data);
    } catch (error) {
      toast.error("Raporlar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const fetchInteractiveReports = async () => {
    try {
      const response = await axios.get(`${API}/interactive-reports`, { withCredentials: true });
      setInteractiveReports(response.data);
    } catch (error) {
      console.error("Interactive reports error:", error);
    }
  };

  const fetchRoyalcertReports = async () => {
    try {
      const response = await axios.get(`${API}/royalcert-audits`, { withCredentials: true });
      setRoyalcertReports(response.data);
    } catch (error) {
      console.error("RoyalCert reports error:", error);
    }
  };

  const fetchAudits = async () => {
    try {
      const response = await axios.get(`${API}/audits`, { withCredentials: true });
      setAudits(response.data);
    } catch (error) {
      console.error("Audits error:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(response.data);
    } catch (error) {
      console.error("Customers error:", error);
    }
  };

  const fetchAuditors = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      // Filter only users with auditor role
      const auditorUsers = response.data.filter(u => 
        u.role === 'auditor' || u.role === 'admin' || u.role === 'superadmin'
      );
      setAuditors(auditorUsers);
    } catch (error) {
      console.error("Auditors error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        audit_id: auditId,
        title: title,
        content: content,
        findings: findings,
        recommendations: recommendations
      };
      await axios.post(`${API}/reports`, payload, { withCredentials: true });
      toast.success("Rapor oluşturuldu");
      setDialogOpen(false);
      resetForm();
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleDelete = async (reportId, reportType) => {
    if (!window.confirm("Bu raporu silmek istediğinizden emin misiniz?")) return;
    try {
      if (reportType === "royalcert") {
        await axios.delete(`${API}/royalcert-audits/${reportId}`, { withCredentials: true });
        toast.success("Rapor silindi");
        fetchRoyalcertReports();
      } else if (reportType === "interactive") {
        await axios.delete(`${API}/interactive-reports/${reportId}`, { withCredentials: true });
        toast.success("Rapor silindi");
        fetchInteractiveReports();
      } else {
        await axios.delete(`${API}/reports/${reportId}`, { withCredentials: true });
        toast.success("Rapor silindi");
        fetchReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silme işlemi başarısız");
    }
  };

  const handleSubmitForReview = async (reportId, reportType) => {
    try {
      if (reportType === "royalcert") {
        await axios.patch(`${API}/royalcert-audits/${reportId}/submit`, {}, { withCredentials: true });
        toast.success("Rapor onaya gönderildi");
        fetchRoyalcertReports();
      } else if (reportType === "interactive") {
        await axios.patch(`${API}/interactive-reports/${reportId}/submit`, {}, { withCredentials: true });
        toast.success("Rapor onaya gönderildi");
        fetchInteractiveReports();
      } else {
        await axios.patch(`${API}/reports/${reportId}/submit`, {}, { withCredentials: true });
        toast.success("Rapor onaya gönderildi");
        fetchReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gönderme başarısız");
    }
  };

  const handleReviewReport = async (reportId, reportType, status, reviewNotes = "") => {
    try {
      // Set loading state for approval with Drive upload
      if (status === "approved" && reportType === "royalcert") {
        setApprovingReportId(reportId);
      }
      
      const endpoint = reportType === "royalcert" 
        ? `${API}/royalcert-audits/${reportId}/review`
        : reportType === "interactive"
        ? `${API}/interactive-reports/${reportId}/review`
        : `${API}/reports/${reportId}/review`;
      
      const response = await axios.patch(endpoint, { status, review_notes: reviewNotes }, { withCredentials: true });
      
      // Check drive upload result for royalcert
      if (status === "approved" && reportType === "royalcert" && response.data?.drive_upload_result) {
        const uploadResult = response.data.drive_upload_result;
        toast.success(
          <div>
            <p className="font-medium">Rapor onaylandı!</p>
            <p className="text-sm">
              {uploadResult.uploaded_files?.length || 0} dosya Drive'a yüklendi
            </p>
            {uploadResult.folder_link && (
              <a 
                href={uploadResult.folder_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                📁 Klasörü Aç
              </a>
            )}
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.success(status === "approved" ? "Rapor onaylandı" : "Rapor reddedildi");
      }
      
      if (reportType === "royalcert") {
        fetchRoyalcertReports();
      } else if (reportType === "interactive") {
        fetchInteractiveReports();
      } else {
        fetchReports();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setApprovingReportId(null);
    }
  };

  const handleCancelApproval = async (reportId, reportType) => {
    if (!window.confirm("Onayı iptal etmek istediğinizden emin misiniz?")) return;
    
    try {
      if (reportType === "royalcert") {
        await axios.patch(`${API}/royalcert-audits/${reportId}/cancel-approval`, {}, { withCredentials: true });
        toast.success("Onay iptal edildi");
        fetchRoyalcertReports();
      }
      // Add other report types if needed
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  // AI Review function
  const handleAIReview = async (reportId) => {
    try {
      setAiReviewLoading(true);
      setAiReviewReportId(reportId);
      setShowAIReview(true);
      
      const response = await axios.post(
        `${API}/reports/${reportId}/ai-review`,
        {},
        { withCredentials: true }
      );
      
      setAiReviewResult(response.data);
      
      if (response.data.status === "ready") {
        toast.success("AI incelemesi tamamlandı - Rapor gönderime hazır!");
      } else if (response.data.status === "ready_with_warnings") {
        toast.info("AI incelemesi tamamlandı - Bazı uyarılar var");
      } else {
        toast.warning("AI incelemesi tamamlandı - Düzenleme önerileri var");
      }
    } catch (error) {
      console.error("AI review error:", error);
      toast.error(error.response?.data?.detail || "AI incelemesi başarısız");
      setAiReviewResult({ error: error.response?.data?.detail || "Bir hata oluştu" });
    } finally {
      setAiReviewLoading(false);
    }
  };

  const resetForm = () => {
    setAuditId("");
    setTitle("");
    setContent("");
    setFindings([]);
    setRecommendations([]);
  };

  const addFinding = () => {
    if (newFindingType && newFindingDesc) {
      setFindings([...findings, { type: newFindingType, description: newFindingDesc }]);
      setNewFindingType("");
      setNewFindingDesc("");
    }
  };

  const removeFinding = (index) => {
    const updated = findings.filter((_, i) => i !== index);
    setFindings(updated);
  };

  const addRecommendation = () => {
    if (newRecommendation) {
      setRecommendations([...recommendations, newRecommendation]);
      setNewRecommendation("");
    }
  };

  const removeRecommendation = (index) => {
    const updated = recommendations.filter((_, i) => i !== index);
    setRecommendations(updated);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-100 text-gray-800",
      submitted: "bg-blue-100 text-blue-800",
      under_review: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    const labels = {
      draft: "Taslak",
      submitted: "Gönderildi",
      under_review: "İnceleniyor",
      approved: "Onaylandı",
      rejected: "Reddedildi"
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Helper function to check if report matches filters
  const matchesFilters = (report, auditData) => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      (report.title || "").toLowerCase().includes(searchLower) ||
      (report.customer_name || "").toLowerCase().includes(searchLower) ||
      (report.audit_title || "").toLowerCase().includes(searchLower) ||
      (report.created_by_name || "").toLowerCase().includes(searchLower);
    
    // Status filter
    const matchesStatus = statusFilter === "all" || report.status === statusFilter;
    
    // Phase filter - get customer's audit_phase
    let matchesPhase = true;
    if (phaseFilter !== "all" && auditData) {
      const customer = customers.find(c => c.customer_id === auditData.customer_id);
      const phase = customer?.audit_phase || "3";
      matchesPhase = phase === phaseFilter;
    }
    
    // Auditor filter
    let matchesAuditor = true;
    if (auditorFilter !== "all" && auditData) {
      matchesAuditor = auditData.assigned_auditor_id === auditorFilter;
    }
    
    // Date filter
    let matchesDate = true;
    const reportDate = report.created_at ? report.created_at.split('T')[0] : '';
    if (startDate && reportDate < startDate) matchesDate = false;
    if (endDate && reportDate > endDate) matchesDate = false;
    
    return matchesSearch && matchesStatus && matchesPhase && matchesAuditor && matchesDate;
  };

  const filteredReports = reports.filter(r => {
    const audit = audits.find(a => a.audit_id === r.audit_id);
    return matchesFilters(r, audit);
  });

  // Combine and filter all reports
  const allReports = [
    ...filteredReports.map(r => ({ ...r, type: "standard" })),
    ...interactiveReports.filter(r => {
      const audit = audits.find(a => a.audit_id === r.audit_id);
      return matchesFilters(r, audit);
    }).map(r => ({ ...r, type: "interactive" })),
    // Add RoyalCert reports
    ...royalcertReports.filter(r => {
      const audit = audits.find(a => a.audit_id === r.audit_id || a.audit_id === r.linked_audit_id);
      return matchesFilters({
        ...r,
        title: r.title || r.audit_title || "RoyalCert Raporu",
        customer_name: r.customer_name || audit?.customer_name || ""
      }, audit);
    }).map(r => {
      // Determine stage from audit_type, certification_type, or is_stage2 flag
      const auditType = r.audit_type || r.auto_fill_data?.audit?.audit_type || r.auto_fill_data?.audit?.certification_type || "";
      const isStage2Flag = r.is_stage2 === true;
      const isStage2Type = auditType.toLowerCase().includes("stage2") || auditType.toLowerCase().includes("2. aşama") || auditType.includes("_2");
      const isStage2 = isStage2Flag || isStage2Type;
      const stageLabel = isStage2 ? "2. Aşama (TR-I)" : "3. Aşama (GSTC)";
      
      return { 
        ...r, 
        type: "royalcert",
        royalcert_stage: stageLabel,
        is_stage2: isStage2,
        report_id: r.audit_record_id,
        title: r.title || r.audit_title || "RoyalCert Raporu",
        created_at: r.created_at || new Date().toISOString(),
        created_by_name: r.created_by_name || r.auto_fill_data?.planning?.audit_team?.team_leader || ""
      };
    })
  ].filter(r => reportType === "all" || r.type === reportType);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">Raporlar</h1>
          <p className="text-[#4B5563]">Denetim raporlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          {tenantSlug === 'royalcert' ? (
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" 
                  data-testid="add-interactive-report-btn"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Yeni Rapor
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Denetim Seçin</DialogTitle>
                  <DialogDescription>
                    Rapor oluşturmak için bir denetim seçin
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Select onValueChange={(value) => {
                    const audit = audits.find(a => a.audit_id === value);
                    if (audit) {
                      // All audits (Stage 2 and Stage 3) use the same full-featured RoyalCertAuditForm
                      navigate(`/audit/royalcert/${value}`);
                    }
                  }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Denetim seçin..." />
                    </SelectTrigger>
                    <SelectContent>
                      {audits.map(audit => {
                        const customerPhase = parseInt(audit.customer_audit_phase) || 2;
                        const auditType = audit.audit_type || '';
                        const isStage3 = auditType.includes('stage3') || auditType.includes('_3') || customerPhase === 3;
                        const stageLabel = isStage3 ? '3. Aşama' : '2. Aşama';
                        
                        return (
                          <SelectItem key={audit.audit_id} value={audit.audit_id}>
                            {audit.customer_name} - {audit.title} ({stageLabel})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </DialogContent>
            </Dialog>
          ) : (
            <Button 
              onClick={() => navigate("/interactive-report/new")}
              className="bg-[#1A4D2E] hover:bg-[#1A4D2E]/90" 
              data-testid="add-interactive-report-btn"
            >
              <ClipboardList className="w-4 h-4 mr-2" />
              Yeni Rapor
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-[#6B7280] -mt-4 mb-4">
        Rapor formatı, denetim seçildiğinde müşterinin aşamasına göre otomatik belirlenir
      </p>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              placeholder="Rapor, müşteri, denetçi ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="report-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="report-status-filter">
              <SelectValue placeholder="Durum filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              <SelectItem value="draft">Taslak</SelectItem>
              <SelectItem value="submitted">Gönderildi</SelectItem>
              <SelectItem value="under_review">İnceleniyor</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Phase Filter */}
          {tenantSlug === 'royalcert' && (
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-500">Aşama:</span>
              <Button
                variant={phaseFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setPhaseFilter("all")}
                className={phaseFilter === "all" ? "bg-[#1A4D2E]" : ""}
              >
                Tümü
              </Button>
              <Button
                variant={phaseFilter === "2" ? "default" : "outline"}
                size="sm"
                onClick={() => setPhaseFilter("2")}
                className={phaseFilter === "2" ? "bg-orange-600" : "border-orange-300 text-orange-600"}
              >
                2. Aşama
              </Button>
              <Button
                variant={phaseFilter === "3" ? "default" : "outline"}
                size="sm"
                onClick={() => setPhaseFilter("3")}
                className={phaseFilter === "3" ? "bg-blue-600" : "border-blue-300 text-blue-600"}
              >
                3. Aşama
              </Button>
            </div>
          )}
          
          {/* Auditor Filter */}
          <Select value={auditorFilter} onValueChange={setAuditorFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Denetçi filtrele" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Denetçiler</SelectItem>
              {auditors.map(a => (
                <SelectItem key={a.user_id} value={a.user_id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Date Filter Row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm text-gray-500 whitespace-nowrap">Tarih:</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-36"
            placeholder="Başlangıç"
          />
          <span className="text-gray-400">-</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-36"
            placeholder="Bitiş"
          />
          {(startDate || endDate || phaseFilter !== "all" || auditorFilter !== "all") && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => { 
                setStartDate(""); 
                setEndDate(""); 
                setPhaseFilter("all"); 
                setAuditorFilter("all"); 
              }}
              className="text-gray-500 px-2"
            >
              Filtreleri Temizle
            </Button>
          )}
        </div>
      </div>

      <Card className="border-[#E8F3EE]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : allReports.length === 0 ? (
            <div className="p-8 text-center text-[#4B5563]">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>{searchTerm ? "Sonuç bulunamadı" : "Henüz rapor yok"}</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead>Rapor</TableHead>
                      <TableHead>Tür</TableHead>
                      <TableHead>Müşteri</TableHead>
                      <TableHead>Oluşturan</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead className="text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allReports.map((report) => (
                      <TableRow key={report.report_id} className="hover:bg-[#E8F3EE]/30">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.type === "interactive" ? "bg-[#1A4D2E]" : "bg-[#1A4D2E]/10"}`}>
                              {report.type === "interactive" ? (
                                <ClipboardList className="w-5 h-5 text-white" />
                              ) : (
                                <FileText className="w-5 h-5 text-[#1A4D2E]" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-[#111827]">{report.title}</p>
                              <p className="text-xs text-[#9CA3AF]">{report.audit_title}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            report.type === "interactive" ? "bg-[#1A4D2E]/10 text-[#1A4D2E]" : 
                            report.type === "royalcert" ? "bg-blue-100 text-blue-600" : 
                            "bg-gray-100 text-gray-600"
                          }`}>
                            {report.type === "interactive" ? "2. Aşama" : 
                             report.type === "royalcert" ? (report.royalcert_stage || "RoyalCert") : 
                             "Standart"}
                          </span>
                        </TableCell>
                        <TableCell>{report.customer_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-[#9CA3AF]" />
                            {report.created_by_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4 text-[#9CA3AF]" />
                            {format(new Date(report.created_at), "dd MMM yyyy", { locale: tr })}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(report.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                if (report.type === "royalcert") {
                                  navigate(`/royalcert-audit/${report.audit_record_id}`);
                                } else if (report.type === "interactive") {
                                  navigate(`/interactive-report/${report.report_id}`);
                                } else {
                                  navigate(`/reports/${report.report_id}`);
                                }
                              }}
                              data-testid={`view-report-${report.report_id}`}
                              title="İncele"
                            >
                              <Eye className="w-4 h-4 text-[#4B5563]" />
                            </Button>
                            {/* AI Review button - only for RoyalCert reports (Stage 3: FR.10/FR.50, Stage 2: FR.04) */}
                            {canReview && (report.status === "submitted" || report.status === "under_review") && 
                             report.type === "royalcert" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleAIReview(
                                  report.type === "royalcert" ? report.audit_record_id : report.report_id
                                )}
                                className="text-blue-600 hover:bg-blue-50"
                                data-testid={`ai-review-btn-${report.report_id || report.audit_record_id}`}
                                title={report.template_id === "royalcert_stage3_audit" ? "AI İncele (FR.10/FR.50)" : "AI İncele (FR.04)"}
                              >
                                <Bot className="w-4 h-4" />
                              </Button>
                            )}
                            {(report.status === "draft" || report.status === "rejected" || !report.status) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleSubmitForReview(report.report_id, report.type)}
                                  data-testid={`submit-report-${report.report_id}`}
                                  title="Onaya Gönder"
                                >
                                  <Send className="w-4 h-4 text-[#1A4D2E]" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDelete(report.report_id, report.type)}
                                  data-testid={`delete-report-${report.report_id}`}
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </>
                            )}
                            {canReview && (report.status === "submitted" || report.status === "under_review") && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleReviewReport(
                                    report.type === "royalcert" ? report.audit_record_id : report.report_id,
                                    report.type,
                                    "approved"
                                  )}
                                  className="text-green-600 hover:bg-green-50"
                                  title={approvingReportId === (report.type === "royalcert" ? report.audit_record_id : report.report_id) 
                                    ? "Drive'a yükleniyor..." 
                                    : "Onayla"}
                                  disabled={approvingReportId !== null}
                                >
                                  {approvingReportId === (report.type === "royalcert" ? report.audit_record_id : report.report_id) ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <CheckCircle className="w-4 h-4" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    const notes = window.prompt("Red gerekçesini girin:");
                                    if (notes !== null) {
                                      handleReviewReport(
                                        report.type === "royalcert" ? report.audit_record_id : report.report_id,
                                        report.type,
                                        "rejected",
                                        notes
                                      );
                                    }
                                  }}
                                  className="text-red-600 hover:bg-red-50"
                                  title="Reddet"
                                  disabled={approvingReportId !== null}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-[#E8F3EE]">
                {allReports.map((report) => (
                  <div key={report.report_id} className="p-4 hover:bg-[#E8F3EE]/30">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${report.type === "interactive" ? "bg-[#1A4D2E]" : "bg-[#1A4D2E]/10"}`}>
                          {report.type === "interactive" ? (
                            <ClipboardList className="w-5 h-5 text-white" />
                          ) : (
                            <FileText className="w-5 h-5 text-[#1A4D2E]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-[#111827] truncate">{report.title}</p>
                          <p className="text-xs text-[#9CA3AF] truncate">{report.audit_title}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {getStatusBadge(report.status)}
                      </div>
                    </div>
                    <div className="mt-3 space-y-2 text-sm text-[#4B5563]">
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${report.type === "interactive" ? "bg-[#1A4D2E]/10 text-[#1A4D2E]" : report.type === "royalcert" ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"}`}>
                          {report.type === "interactive" ? "2. Aşama" : 
                           report.type === "royalcert" ? (report.royalcert_stage || "RoyalCert") : 
                           "Standart"}
                        </span>
                        <span className="text-xs text-[#9CA3AF]">
                          {format(new Date(report.created_at), "dd MMM yyyy", { locale: tr })}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-[#9CA3AF] flex-shrink-0" />
                        <span className="truncate">{report.customer_name}</span>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 pt-2 border-t border-[#E8F3EE]">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (report.type === "royalcert") {
                            navigate(`/royalcert-audit/${report.audit_record_id}`);
                          } else if (report.type === "interactive") {
                            navigate(`/interactive-report/${report.report_id}`);
                          } else {
                            navigate(`/reports/${report.report_id}`);
                          }
                        }}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Görüntüle
                      </Button>
                      {(report.status === "draft" || report.status === "rejected") && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSubmitForReview(
                              report.type === "royalcert" ? report.audit_record_id : report.report_id,
                              report.type
                            )}
                            className="h-8 w-8"
                            title="Onaya Gönder"
                          >
                            <Send className="w-4 h-4 text-[#1A4D2E]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(
                              report.type === "royalcert" ? report.audit_record_id : report.report_id,
                              report.type
                            )}
                            className="h-8 w-8"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </>
                      )}
                      {(report.status === "submitted" || report.status === "under_review") && canReview && (
                        <>
                          {/* AI Review Button - only for RoyalCert reports (Stage 3: FR.10/FR.50, Stage 2: FR.04) */}
                          {report.type === "royalcert" && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAIReview(
                                report.type === "royalcert" ? report.audit_record_id : report.report_id
                              )}
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              data-testid={`ai-review-btn-${report.report_id || report.audit_record_id}`}
                            >
                              <Bot className="w-4 h-4 mr-1" />
                              AI İncele
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReviewReport(
                              report.type === "royalcert" ? report.audit_record_id : report.report_id,
                              report.type,
                              "approved"
                            )}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            disabled={approvingReportId !== null}
                          >
                            {approvingReportId === (report.type === "royalcert" ? report.audit_record_id : report.report_id) ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Drive'a Yükleniyor...
                              </>
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Onayla
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const notes = window.prompt("Red gerekçesini girin:");
                              if (notes !== null) {
                                handleReviewReport(
                                  report.type === "royalcert" ? report.audit_record_id : report.report_id,
                                  report.type,
                                  "rejected",
                                  notes
                                );
                              }
                            }}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reddet
                          </Button>
                        </>
                      )}
                      {report.status === "approved" && user?.role === "super_admin" && report.type === "royalcert" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelApproval(report.audit_record_id, report.type)}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        >
                          Onayı İptal Et
                        </Button>
                      )}
                      {report.status === "rejected" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(
                            report.type === "royalcert" ? `/royalcert-audit/${report.audit_record_id}` :
                            report.type === "interactive" ? `/interactive-report/${report.report_id}` : 
                            `/reports/${report.report_id}`
                          )}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Düzenle
                        </Button>
                      )}
                    </div>
                    {report.status === "rejected" && report.review_notes && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
                        <span className="font-medium">Red Gerekçesi:</span> {report.review_notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* AI Review Dialog */}
      <Dialog open={showAIReview} onOpenChange={setShowAIReview}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-blue-600" />
              AI Rapor İncelemesi
            </DialogTitle>
            <DialogDescription>
              OpenAI GPT-5 mini ile rapor analizi
            </DialogDescription>
          </DialogHeader>
          
          {aiReviewLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Rapor AI tarafından inceleniyor...</p>
              <p className="text-sm text-gray-400 mt-2">Bu işlem birkaç saniye sürebilir</p>
            </div>
          ) : aiReviewResult?.error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{aiReviewResult.error}</p>
            </div>
          ) : aiReviewResult ? (
            <div className="space-y-6">
              {/* Overall Score */}
              <div className={`p-4 rounded-lg border-2 ${
                aiReviewResult.status === "ready" ? "bg-green-50 border-green-300" :
                aiReviewResult.status === "ready_with_warnings" ? "bg-yellow-50 border-yellow-300" :
                "bg-red-50 border-red-300"
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">Genel Puan</h3>
                    <p className="text-sm text-gray-600">{aiReviewResult.summary_tr}</p>
                  </div>
                  <div className={`text-4xl font-bold ${
                    aiReviewResult.overall_score >= 80 ? "text-green-600" :
                    aiReviewResult.overall_score >= 60 ? "text-yellow-600" :
                    "text-red-600"
                  }`}>
                    {aiReviewResult.overall_score}
                  </div>
                </div>
              </div>
              
              {/* Checks */}
              <div className="grid grid-cols-2 gap-4">
                {aiReviewResult.checks && Object.entries(aiReviewResult.checks).map(([key, check]) => (
                  <div key={key} className={`p-3 rounded-lg border ${
                    check.passed ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">
                        {key === "completeness" && "Tamlık"}
                        {key === "consistency" && "Tutarlılık"}
                        {key === "language_quality" && "Dil Kalitesi"}
                        {key === "standards_compliance" && "Standart Uyumu"}
                      </span>
                      <span className={`font-bold ${
                        check.score >= 80 ? "text-green-600" : 
                        check.score >= 60 ? "text-yellow-600" : "text-red-600"
                      }`}>
                        {check.score}/100
                      </span>
                    </div>
                    {check.issues?.length > 0 && (
                      <ul className="text-xs text-gray-600 space-y-1">
                        {check.issues.slice(0, 3).map((issue, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <AlertTriangle className="w-3 h-3 text-yellow-500 mt-0.5 flex-shrink-0" />
                            {issue}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Suggestions */}
              {aiReviewResult.suggestions?.length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    İyileştirme Önerileri
                  </h4>
                  <div className="space-y-2">
                    {aiReviewResult.suggestions.map((suggestion, idx) => (
                      <div key={idx} className={`p-2 rounded text-sm flex items-start gap-2 ${
                        suggestion.priority === "high" ? "bg-red-50 border-l-4 border-red-400" :
                        suggestion.priority === "medium" ? "bg-yellow-50 border-l-4 border-yellow-400" :
                        "bg-blue-50 border-l-4 border-blue-400"
                      }`}>
                        <span className="flex-1">{suggestion.message}</span>
                        <Badge variant="outline" className="text-xs">
                          {suggestion.category}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* English Summary */}
              {aiReviewResult.summary_en && (
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="text-gray-500 text-xs mb-1">English Summary:</p>
                  <p className="text-gray-700">{aiReviewResult.summary_en}</p>
                </div>
              )}
            </div>
          ) : null}
          
          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAIReview(false)}>
              Kapat
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
